import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, ArrowLeft } from 'lucide-react';

/**
 * Shared password-set page used for both:
 *   - /auth/setup  (initial password after register)
 *   - /auth/reset  (forgot-password reset)
 *
 * The mode is inferred from the path (kind="setup"|"reset") so we can swap copy
 * and POST to the right endpoint. On success the backend returns a TokenResponse
 * which we use to auto-login the caller.
 */
const SetPassword = ({ kind }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const heading = kind === 'setup' ? 'Set your password' : 'Choose a new password';
  const subhead = kind === 'setup'
    ? 'Pick a strong password — at least 8 characters.'
    : 'Pick a new password — at least 8 characters. This will replace your old one.';
  const ctaCopy = kind === 'setup' ? 'Activate account' : 'Reset password';
  const endpoint = kind === 'setup' ? '/auth/setup-password' : '/auth/reset-password';
  const passwordField = kind === 'setup' ? 'password' : 'new_password';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!token) {
      toast.error('Missing token — please use the link from your email');
      return;
    }
    setLoading(true);
    try {
      const payload = { token, [passwordField]: password };
      const r = await axios.post(`${API}${endpoint}`, payload);
      login(r.data.access_token, r.data.user);
      toast.success(kind === 'setup' ? 'Account activated — welcome!' : 'Password reset — you are signed in');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not complete the request');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid={`${kind}-page`}>
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm p-8">
          <h1 className="text-2xl font-chivo font-bold text-slate-900 mb-2">Missing token</h1>
          <p className="text-sm font-ibm text-slate-600 mb-6">
            This page needs a one-time token from the email we sent you. Open the link in that email,
            or request a new one if it expired.
          </p>
          <Link
            to={kind === 'reset' ? '/auth/forgot' : '/auth'}
            data-testid={`${kind}-no-token-cta`}
            className="text-sm font-ibm text-slate-900 underline"
          >
            {kind === 'reset' ? 'Request a new reset link' : 'Back to sign in'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid={`${kind}-page`}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <div className="bg-white border border-slate-200 rounded-sm p-8">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={20} className="text-slate-900" />
            <h1 className="text-2xl font-chivo font-bold text-slate-900">{heading}</h1>
          </div>
          <p className="text-sm text-slate-600 font-ibm mb-6">{subhead}</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${kind}-form`}>
            <div>
              <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                New password
              </Label>
              <Input
                data-testid={`${kind}-password-input`}
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                Confirm
              </Label>
              <Input
                data-testid={`${kind}-confirm-input`}
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
              />
            </div>
            <Button
              type="submit"
              data-testid={`${kind}-submit-button`}
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11"
            >
              {loading ? 'Working…' : ctaCopy}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/auth"
              data-testid={`${kind}-back-to-login`}
              className="text-sm font-ibm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SetPassword;
